import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { machineryEventTypeService } from '../../../services/machineryEventTypeService';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';

const EventTypesConfig = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [newCategory, setNewCategory] = useState('informativo');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editCategory, setEditCategory] = useState('informativo');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadTypes = async () => {
    setLoading(true);
    try {
      const res = await machineryEventTypeService.getEventTypes();
      if (res.success && Array.isArray(res.data)) {
        setTypes(res.data);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Error al cargar tipos de evento';
      setToast({ isOpen: true, message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = (newLabel || '').trim();
    if (!name) {
      setToast({ isOpen: true, message: 'El nombre no puede estar vacío', type: 'error' });
      return;
    }
    setAdding(true);
    try {
      const res = await machineryEventTypeService.createEventType({ name, category: newCategory });
      if (res.success) {
        setNewLabel('');
        setNewCategory('informativo');
        setToast({ isOpen: true, message: res.message || 'Tipo de evento agregado', type: 'success' });
        await loadTypes();
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al agregar', type: 'error' });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Error al agregar';
      setToast({ isOpen: true, message: msg, type: 'error' });
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValue(item.name || '');
    setEditCategory(item.category === 'monetario' ? 'monetario' : 'informativo');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setEditCategory('informativo');
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    const name = (editValue || '').trim();
    if (!name) {
      setToast({ isOpen: true, message: 'El nombre no puede estar vacío', type: 'error' });
      return;
    }
    try {
      const res = await machineryEventTypeService.updateEventType(editingId, { name, category: editCategory });
      if (res.success) {
        setEditingId(null);
        setEditValue('');
        setToast({ isOpen: true, message: res.message || 'Tipo de evento actualizado', type: 'success' });
        await loadTypes();
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al actualizar', type: 'error' });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Error al actualizar';
      setToast({ isOpen: true, message: msg, type: 'error' });
    }
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const res = await machineryEventTypeService.deleteEventType(itemToDelete.id);
      if (res.success) {
        setToast({ isOpen: true, message: res.message || 'Tipo de evento eliminado', type: 'success' });
        await loadTypes();
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al eliminar', type: 'error' });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Error al eliminar';
      setToast({ isOpen: true, message: msg, type: 'error' });
    }
    setIsDeleteModalOpen(false);
    setItemToDelete(null);
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link to="/machinery/events" className="text-sm text-teal-600 hover:text-teal-800 mb-1 inline-block">← Volver a Bitacora</Link>
          <h1 className="text-2xl font-bold text-gray-800">Configuración de tipos de evento</h1>
          <p className="text-gray-600 mt-1">Gestione los tipos de evento disponibles en la bitácora (maquinaria y vehículos)</p>
        </div>
        <Link
          to="/machinery/events"
          className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium transition-colors"
        >
          Ver eventos
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Agregar tipo de evento</h2>
        <form onSubmit={handleAdd} className="flex gap-3 flex-wrap items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Ej. Inspección, Cambio de aceite"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="min-w-[180px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="informativo">Informativo</option>
              <option value="monetario">Monetario</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={adding}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md text-sm font-medium transition-colors"
          >
            {adding ? 'Agregando...' : 'Agregar'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">Tipos configurados</h2>
        </div>
        {loading ? (
          <div className="px-6 py-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            <p className="mt-2 text-gray-600">Cargando...</p>
          </div>
        ) : types.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No hay tipos de evento configurados. Agregue uno arriba.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {types.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === item.id ? (
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          autoFocus
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{item.name}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === item.id ? (
                        <select
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        >
                          <option value="informativo">Informativo</option>
                          <option value="monetario">Monetario</option>
                        </select>
                      ) : (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${(item.category || 'informativo') === 'monetario' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
                          {(item.category || 'informativo') === 'monetario' ? 'Monetario' : 'Informativo'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {editingId === item.id ? (
                        <span className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={saveEdit}
                            className="text-teal-600 hover:text-teal-900"
                          >
                            Guardar
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Cancelar
                          </button>
                        </span>
                      ) : (
                        <span className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            className="text-teal-600 hover:text-teal-900"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Eliminar
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setItemToDelete(null); }}
        onConfirm={confirmDelete}
        title="Eliminar tipo de evento"
        message={itemToDelete ? `¿Eliminar "${itemToDelete.name}"? Los eventos existentes que usen este tipo no se modificarán.` : '¿Eliminar este tipo?'}
      />
      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, isOpen: false })} />
    </div>
  );
};

export default EventTypesConfig;
